/**
 * ExternalEditionGate — 외부용 화면일 때 ① 열어 준 기능 밖으로 못 나가게 막고
 * ② 주소에 외부용 표(`?edition=external`)가 늘 붙어 있게 지킨다.
 *
 * 보호 라우트 전체의 입구에 딱 한 번 끼운다(LeadDemoGate 바로 옆). 라우트가 100개든 200개든
 * 외부용 사용자는 반드시 여기를 지나므로, **새 화면이 생겨도 자동으로 막힌다** — 화면마다
 * 가드를 붙이는 방식이면 새로 만든 사람이 잊는 순간 구멍이 난다.
 *
 * ## ① 막기
 * 메뉴에서 감추는 것만으로는 부족하다. 주소창에 /crm/customers 를 그대로 쳐 넣으면 열리기
 * 때문이다. 그건 외부 고객에게 우리 내부 화면이 열린다는 뜻이라 사고다. 그래서 **메뉴에서
 * 빼는 일(AppSidebar)과 주소를 막는 일(여기)을 둘 다** 하고, 둘 다 같은 것을 본다 —
 * 카탈로그(shared/external-edition.ts) + 관리자가 켜 둔 목록(EditionContext.enabledFeatures).
 * 판정이 한 군데라 둘이 어긋날 수 없다.
 *
 * ## ② 주소에 표 지키기
 * 외부용으로 보고 있는데 주소에 표가 없으면 **여기서 붙인다.** 메뉴 링크를 누르거나 화면이
 * 스스로 이동하면 쿼리가 떨어져 나가는데, 그때마다 표가 사라지면 "외부용 화면의 주소"라는
 * 약속이 깨진다 — 스크린챗이 그 링크만 보고 어느 얼굴인지 가릴 수 없게 된다.
 * 실제 고객(전환 권한이 없는 계정)에게도 붙는다. 그들이 보내는 티켓이야말로 외부용 화면에서
 * 오는 것이므로 표가 가장 필요하다.
 * 되돌아갈 자리를 남기지 않도록 replace 로 갈아끼운다(뒤로가기가 표 없는 같은 주소로
 * 돌아가면 곧바로 다시 튕겨 무한 왕복이 된다).
 *
 * ## ③ 열린 기능이 하나도 없을 때
 * 기능 스위치를 DB 로 옮기면서 **아무것도 안 켜진 상태**가 실제로 가능해졌다(관리자가 전부
 * 끄거나, 목록 조회가 실패해 빈 배열로 내려온 경우). 그때 착지점은 "/" 인데 "/" 는 늘 열린
 * 경로라 통과하고, HomeRedirect 가 다시 착지점을 계산해 "/" 로 보내 **무한 왕복**이 된다.
 * 그래서 그 경우는 리다이렉트하지 않고 안내를 그린다.
 *
 * ⚠️ 화면 울타리이지 그것만으로 보안 경계는 아니다 — 서버도 같은 카탈로그로 API 를 막고
 *    (server/src/lib/auth-middleware.ts §외부 배포판 잠금), 데이터 격리는 권한·매장 스코프가 한다.
 */

import { Navigate, Outlet, useLocation, useNavigate } from "react-router";
import {
  isExternalSearch,
  useEdition,
  withExternalSearch,
  withoutExternalSearch,
} from "../../context/EditionContext";
import {
  externalHomePath,
  isExternalPathAllowed,
} from "../../../shared/external-edition";
import Button from "../ui/button/Button";

/** 외부용인데 열린 기능이 하나도 없을 때 — 보낼 곳이 없으므로 그 자리에 안내를 그린다. */
function NoExternalFeatures({ canSwitch }: { canSwitch: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-2 px-6 text-center">
      <p className="text-base font-semibold text-gray-800 dark:text-gray-100">
        사용할 수 있는 기능이 아직 없습니다
      </p>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        관리자가 기능을 열어 주면 이 자리에 나타납니다.
      </p>
      {canSwitch && (
        <Button
          size="sm"
          className="mt-4"
          onClick={() =>
            navigate({
              pathname: "/admin/external-features",
              search: withoutExternalSearch(location.search),
            })
          }
        >
          선택 이용 매장에 열어 줄 기능 고르기
        </Button>
      )}
    </div>
  );
}

export default function ExternalEditionGate() {
  const { edition, canSwitch, enabledFeatures, ready } = useEdition();
  const location = useLocation();

  // 신분이 확정되기 전에는 아무것도 하지 않는다 — 여기서 주소를 고치면 라인업 계정이
  // 새로고침할 때마다 체험 화면으로 굳는다(EditionContext 의 ready 주석이 그 함정의 진본).
  if (!ready || edition !== "partial") return <Outlet />;

  // ③ 보낼 곳이 없으면 보내지 않는다(위 머리말 참조).
  if (enabledFeatures.length === 0) return <NoExternalFeatures canSwitch={canSwitch} />;

  // ① 열어 주지 않은 화면이면 첫 기능으로 돌린다(표를 붙인 채로).
  if (!isExternalPathAllowed(location.pathname, enabledFeatures)) {
    return (
      <Navigate
        to={{ pathname: externalHomePath(enabledFeatures), search: withExternalSearch("") }}
        replace
      />
    );
  }

  // ② 열린 화면이지만 주소에 표가 빠졌으면 붙인다(그 외 쿼리는 그대로 둔다).
  if (!isExternalSearch(location.search)) {
    return (
      <Navigate
        to={{ pathname: location.pathname, search: withExternalSearch(location.search) }}
        replace
      />
    );
  }

  return <Outlet />;
}
